import { z } from 'zod';
import groupBy from 'lodash-es/groupBy.js';
import { djs, type Dayjs } from '@duke-hq/djs';
import {
  StudentResourceEnrolmentStatus,
  StudentWorkSchedule,
  CourseKind,
  UnitOfCompetency,
} from '@duke-hq/svc-api-db/client';
import { getCourseDatesFromCourseEnrolmentScheduleItems } from '@duke-hq/lib/dates';
import { CourseSegment, isCourseSegment } from '@duke-hq/shared-constants/enrolments';

const RTO_TZ = 'Australia/Sydney';

export const DocumentTypeEnum = z.enum([
  'educationVerification',
  'progressReport',
  'attendanceReport',
  'statementOfAttainment',
  'certificateAndRecordOfResults',
  'nonVocationalCertificate',
  'courseCatalog',
  'confirmationOfEnrolment',
  'assessmentReport',
  'studentTimesheet',
]);
export type DocumentType = z.infer<typeof DocumentTypeEnum>;

type CompletedCourseEnrolmentScheduleItem = {
  semester: number;
  position: number;
  item_id: string;
  item_type: string;
  unit_of_competency_id: string;
  is_complete: boolean;
  admin_course_schedule_week: number;
  scheduled_due_date: Date;
};

type UnitAssessmentEnrolmentSubset = {
  completed_at: Date | null;
  lastActivityAt: Date | null;
  status: StudentResourceEnrolmentStatus;
  unit_assessment_id: string;
};

type ResourceEnrolmentSubset = {
  completed_at: Date | null;
  lastActivityAt: Date | null;
  status: StudentResourceEnrolmentStatus;
  resource_id: string;
};

type Enrolment = {
  user: {
    first_name: string;
    last_name: string | null;
    studentData: {
      universal_student_identifier: string | null;
      date_of_birth: Date | null;
      avetmiss_identifier: string | null;
    } | null;
  };
  unitAssessmentEnrolmentIdMap: Map<string, UnitAssessmentEnrolmentSubset>;
  resourceEnrolmentIdMap: Map<string, ResourceEnrolmentSubset>;
  courseEnrolmentScheduleItems: CompletedCourseEnrolmentScheduleItem[];
  id: number;
  course_id: string;
  work_schedule: StudentWorkSchedule;
  course_segment: string;
  start_at_date: Date;
  completed_at: Date | null;
  withdrawn_at: Date | null;
  progress_percent: number;
  semester_2_start_at_date?: Date | null;
  semesterDueDate?: Date | null;
};

type Course = {
  identifier: string | null;
  title: string;
  courseItems: {
    unitOfCompetency: {
      id: string;
      code: string;
      title: string;
      unitAssessment?: {
        id: string;
      } | null;
    } | null;
  }[];
  schedule: unknown;
  kind: CourseKind;
  derivedFromCourse: {
    id: string;
    title: string;
    identifier: string | null;
  } | null;
};

export const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  educationVerification: 'Education Verification',
  progressReport: 'Progress Report',
  attendanceReport: 'Attendance Report',
  statementOfAttainment: 'Statement of Attainment',
  certificateAndRecordOfResults: 'Certificate and Record of Results',
  nonVocationalCertificate: 'Certificate and Record of Results',
  courseCatalog: 'Course Catalogue',
  confirmationOfEnrolment: 'Confirmation of Enrolment',
  assessmentReport: 'Assessment Audit Report',
  studentTimesheet: 'Student Timesheet',
} as const;

/**
 * Determines the actual completion date for a specific semester based on when
 * the last UnitAssessmentEnrolment was marked as complete.
 *
 * If the semester in not fully complete, then this function will return `null`.
 *
 * @param {number} semester - The semester number to check for completion.
 * @param {Enrolment} enrolment - The enrolment object containing course schedule items and assessment data.
 * @returns {Dayjs | null} - The latest completion date for the semester if it is complete, otherwise `null`.
 */
function getSemesterCompletionDate(semester: number, enrolment: Enrolment): Dayjs | null {
  const scheduleItemsBySemester = enrolment.courseEnrolmentScheduleItems.reduce<
    Record<number, CompletedCourseEnrolmentScheduleItem[]>
  >((acc, item) => {
    let itemsForSemester = acc[item.semester];
    if (!itemsForSemester) {
      itemsForSemester = acc[item.semester] = [];
    }

    itemsForSemester.push(item);
    return acc;
  }, {});

  // UNIT rows are structural placeholders that are never marked complete;
  // Exclude them from the completeness check.
  const scheduleItems = (scheduleItemsBySemester[semester] ?? []).filter(
    (item) => item.item_type !== 'UNIT',
  );
  const isSemesterComplete = scheduleItems.length > 0 && scheduleItems.every((i) => i.is_complete);

  return isSemesterComplete
    ? djs.max(
        scheduleItems
          .map(({ item_id }) => {
            const unitAssessmentEnrolment = enrolment.unitAssessmentEnrolmentIdMap.get(item_id);
            if (!unitAssessmentEnrolment) {
              return null;
            }
            return djs(
              unitAssessmentEnrolment.completed_at ?? unitAssessmentEnrolment.lastActivityAt,
            );
          })
          .filter((date) => date !== null),
      )
    : null;
}

/**
 * Returns the full course name for a course.
 * For short courses, it returns the derived course's identifier and title.
 * For other courses, it returns the course's identifier and title.
 *
 * @param course - The course to get the full name for.
 * @returns An object containing the identifier and title of the course.
 */
function getFullCourseNameForDocument(course: Course) {
  if (course.kind === CourseKind.SHORT_COURSE) {
    const derived = course.derivedFromCourse;

    return {
      identifier: derived?.identifier ?? '',
      title: derived?.title ?? '',
    };
  }

  return {
    identifier: course.identifier ?? '',
    title: course.title,
  };
}

export default function formatPdfDocumentData({
  documentType,
  enrolment,
  course,
  completedUnitsOfCompetency,
  semester,
  userUTCOffset,
}: {
  documentType: DocumentType;
  enrolment: Enrolment;
  course: Course;
  completedUnitsOfCompetency: Pick<UnitOfCompetency, 'id' | 'code' | 'title'>[];
  semester?: number;
  userUTCOffset?: number;
}) {
  const fullStudentName = [enrolment.user.first_name, enrolment.user.last_name]
    .filter(Boolean)
    .join(' ');

  const { identifier, title } = getFullCourseNameForDocument(course);
  const fullCourseName = `${identifier} ${title}`.trim();
  const studySchedule =
    enrolment.work_schedule === StudentWorkSchedule.FULL_TIME ? 'Full-time' : 'Part-time';
  const { unitAssessmentEnrolmentIdMap } = enrolment;
  const completedUnitOfCompetencyIds = completedUnitsOfCompetency.map((unit) => unit.id);

  // Semester and course end dates follow the student's actual unit due dates
  // (never earlier than the formula); start dates stay on the formula. This is
  // handled inside getCourseDatesFromCourseEnrolmentScheduleItems.
  const expectedCourseStartAndEndDates = getCourseDatesFromCourseEnrolmentScheduleItems({
    courseEnrolmentScheduleItems: enrolment.courseEnrolmentScheduleItems,
    courseSegment: enrolment.course_segment,
    workSchedule: enrolment.work_schedule ?? StudentWorkSchedule.FULL_TIME,
    startAt: enrolment.start_at_date,
    semesterTwoStartAt: enrolment.semester_2_start_at_date,
  });

  const currentDate = djs().utcOffset(userUTCOffset ?? 0);

  if (documentType === 'progressReport') {
    // we cap the progress report date so that it can't go on forever
    const maxProgressReportDate =
      djs.min([
        ...(enrolment.withdrawn_at ? [djs(enrolment.withdrawn_at)] : []),
        ...(enrolment.completed_at ? [djs(enrolment.completed_at)] : []),
        currentDate,
      ]) ?? currentDate;
    // Count weeks between RTO-timezone calendar dates so the value rolls over at
    // local midnight (a raw instant diff rolls over at midnight UTC, i.e. 10am AEST).
    const maxProgressReportDateInRtoTz = maxProgressReportDate.tz(RTO_TZ);
    const progressWeeks = djs
      .utc(maxProgressReportDateInRtoTz.format('YYYY-MM-DD'))
      .diff(djs.utc(enrolment.start_at_date).format('YYYY-MM-DD'), 'week');
    const completionDate = enrolment.completed_at
      ? djs(enrolment.completed_at).tz(RTO_TZ).format('DD/MM/YYYY')
      : '';

    const progressPercentage = enrolment.progress_percent;

    return {
      name: fullStudentName,
      course: fullCourseName,
      USI: enrolment.user?.studentData?.universal_student_identifier,
      dob: enrolment.user?.studentData?.date_of_birth
        ? djs(enrolment.user?.studentData?.date_of_birth).format('DD/MM/YYYY')
        : 'N/A',
      startDate: djs(enrolment.start_at_date).format('DD/MM/YYYY'),
      signatureDate: maxProgressReportDateInRtoTz.format('DD/MM/YYYY'),
      completionDate,
      studySchedule,
      progressWeeks,
      progressPercentage,
    };
  }

  if (documentType === 'statementOfAttainment') {
    const competenciesWithDates = completedUnitsOfCompetency
      .map((unit) => {
        const matchingScheduleItem = enrolment.courseEnrolmentScheduleItems.find(
          (item) => item.unit_of_competency_id === unit.id && item.item_type === 'ASSESSMENT',
        );

        const unitAssessmentEnrolment = matchingScheduleItem
          ? unitAssessmentEnrolmentIdMap.get(matchingScheduleItem.item_id)
          : null;

        let result = 'N/A';
        if (unitAssessmentEnrolment?.status === StudentResourceEnrolmentStatus.COMPLETED) {
          result = 'Competent';
        } else if (
          unitAssessmentEnrolment?.status === StudentResourceEnrolmentStatus.CREDIT_TRANSFER
        ) {
          result = 'Credit Transfer';
        } else if (
          unitAssessmentEnrolment?.status ===
          StudentResourceEnrolmentStatus.RECOGNITION_OF_PRIOR_LEARNING
        ) {
          result = 'RPL';
        }

        return {
          id: unit.id,
          code: unit.code,
          title: unit.title,
          result,
          completed_at: unitAssessmentEnrolment?.completed_at ?? currentDate.toDate(),
        };
      })
      .sort((a, b) => djs(a.completed_at).valueOf() - djs(b.completed_at).valueOf());

    const lastCompletedAt = competenciesWithDates.at(-1)?.completed_at;
    const signatureDate = lastCompletedAt ? djs(lastCompletedAt) : currentDate;

    return {
      name: fullStudentName,
      course: fullCourseName,
      completionDate: signatureDate.tz(RTO_TZ).format('DD/MM/YYYY'),
      signatureDate: signatureDate.tz(RTO_TZ).format('DD/MM/YYYY'),
      avetmissIdentifier: enrolment.user?.studentData?.avetmiss_identifier,
      courseEnrolmentId: enrolment.id,
      competencies: competenciesWithDates.map(({ completed_at, ...unit }) => unit),
    };
  }

  if (documentType === 'educationVerification') {
    if (semester == null || ![1, 2].includes(semester)) {
      throw new Error('A VOE can only be generated for semester 1 or 2');
    }
    const semesterDates = expectedCourseStartAndEndDates.semesters.find(
      (semesterDate) => semesterDate.semester === semester,
    );
    const actualSemesterCompletionDate = getSemesterCompletionDate(semester, enrolment);
    const scheduledSemesterEndDate = semesterDates?.semesterEndDate.rawDate
      ? djs(semesterDates.semesterEndDate.rawDate)
      : null;

    // Both the semester end date and the signature date print the later
    // of the semester end date and the date the student actually finished.
    // Students who finish on time or early keep the scheduled date.
    const semesterEndOrCompletionDate = djs.max(
      [scheduledSemesterEndDate, actualSemesterCompletionDate].filter((date) => date !== null),
    );
    const formattedSemesterEndDate = semesterEndOrCompletionDate
      ? semesterEndOrCompletionDate.tz(RTO_TZ).format('DD/MM/YYYY')
      : null;

    return {
      name: fullStudentName,
      course: fullCourseName,
      USI: enrolment.user?.studentData?.universal_student_identifier,
      dob: djs(enrolment.user?.studentData?.date_of_birth).format('DD/MM/YYYY'),
      startDate: djs(enrolment.start_at_date).format('DD/MM/YYYY'),
      studySchedule,
      semesters:
        isCourseSegment(enrolment.course_segment) &&
        enrolment.course_segment === CourseSegment.FULL_COURSE
          ? 2
          : 1,
      semester: semester,
      semesterStartDate: semesterDates?.semesterStartDate.short ?? 'N/A',
      semesterEndDate: formattedSemesterEndDate ?? 'N/A',
      weeklyHours: 20,
      passed: true,
      ...(formattedSemesterEndDate ? { signatureDate: formattedSemesterEndDate } : {}),
    };
  }

  if (documentType === 'certificateAndRecordOfResults') {
    const completedScheduleItems = enrolment.courseEnrolmentScheduleItems.filter((item) => {
      return item.item_type === 'ASSESSMENT' && item.is_complete && item.semester !== 0;
    });
    // Pick the later of the 2 dates in case the course is finished early.
    // The earliest date we can use here is the expected course completion date
    const signatureDate = djs.max([
      djs(expectedCourseStartAndEndDates.courseEndDate?.rawDate),
      djs(enrolment.completed_at),
    ]);

    return {
      name: fullStudentName,
      course: fullCourseName,
      completionDate: djs(enrolment.completed_at || new Date())
        .tz(RTO_TZ)
        .format('DD/MM/YYYY'),
      avetmissIdentifier: enrolment.user?.studentData?.avetmiss_identifier,
      courseEnrolmentId: enrolment.id,
      signatureDate: signatureDate?.tz(RTO_TZ).format('DD/MM/YYYY'),
      units: completedScheduleItems.map((courseEnrolmentScheduleItem) => {
        const unitAssessmentEnrolment =
          unitAssessmentEnrolmentIdMap.get(courseEnrolmentScheduleItem.item_id) ?? null;
        const unitOfCompetency = completedUnitsOfCompetency.find(
          (unitOfCompetency) =>
            unitOfCompetency.id === courseEnrolmentScheduleItem.unit_of_competency_id,
        );

        const year: string = unitAssessmentEnrolment
          ? djs(unitAssessmentEnrolment.completed_at ?? unitAssessmentEnrolment.lastActivityAt)
              .tz(RTO_TZ)
              .format('YYYY')
          : 'N/A';

        let result = 'N/A';
        if (unitAssessmentEnrolment?.status === StudentResourceEnrolmentStatus.COMPLETED) {
          result = 'Competent';
        } else if (
          unitAssessmentEnrolment?.status === StudentResourceEnrolmentStatus.CREDIT_TRANSFER
        ) {
          result = 'Credit Transfer';
        } else if (
          unitAssessmentEnrolment?.status ===
          StudentResourceEnrolmentStatus.RECOGNITION_OF_PRIOR_LEARNING
        ) {
          result = 'RPL';
        }

        return {
          unitCode: unitOfCompetency?.code,
          unitTitle: unitOfCompetency?.title,
          result,
          year,
        };
      }),
    };
  }

  if (documentType === 'nonVocationalCertificate') {
    const signatureDate = djs(enrolment.completed_at);

    const nonVocResources = enrolment.courseEnrolmentScheduleItems.filter((item) => {
      return (
        (item.item_type === 'ASSESSMENT' || item.item_type === 'CONVERSATION') &&
        completedUnitOfCompetencyIds.includes(item.unit_of_competency_id)
      );
    });

    const orderedUnitIds = [
      ...new Set(
        nonVocResources
          .sort((a, b) => a.position - b.position)
          .map((item) => item.unit_of_competency_id),
      ),
    ];

    const resourcesByUnitOfCompetencyId = groupBy(nonVocResources, 'unit_of_competency_id');

    return {
      name: fullStudentName,
      course: fullCourseName,
      completionDate: djs(enrolment.completed_at || new Date())
        .tz(RTO_TZ)
        .format('DD/MM/YYYY'),
      avetmissIdentifier: enrolment.user?.studentData?.avetmiss_identifier,
      courseEnrolmentId: enrolment.id,
      signatureDate: signatureDate?.tz(RTO_TZ).format('DD/MM/YYYY'),

      units: orderedUnitIds.map((unitOfCompetencyId) => {
        const unitResources = resourcesByUnitOfCompetencyId[unitOfCompetencyId] ?? [];
        const conversationResources = unitResources
          .filter((item) => item.item_type === 'CONVERSATION')
          .map(({ item_id }) => {
            return enrolment.resourceEnrolmentIdMap.get(item_id) ?? null;
          })
          .filter((item): item is ResourceEnrolmentSubset => item !== null);

        const assessmentResources = unitResources
          .filter((item) => item.item_type === 'ASSESSMENT')
          .map(({ item_id }) => {
            return unitAssessmentEnrolmentIdMap.get(item_id) ?? null;
          })
          .filter((item): item is UnitAssessmentEnrolmentSubset => item !== null);

        const assessmentCompletionDates: Dayjs[] = assessmentResources.map(
          ({ completed_at, lastActivityAt }) => {
            return djs(completed_at ?? lastActivityAt);
          },
        );

        const conversationCompletionDates: Dayjs[] = conversationResources.map(
          ({ completed_at, lastActivityAt }) => {
            return djs(completed_at ?? lastActivityAt);
          },
        );

        const year: string = (
          djs.max(...assessmentCompletionDates, ...conversationCompletionDates) ??
          djs(enrolment.completed_at)
        )
          .tz(RTO_TZ)
          .format('YYYY');

        const unitOfCompetency = completedUnitsOfCompetency.find(
          (unitOfCompetency) => unitOfCompetency.id === unitOfCompetencyId,
        );

        let result = 'N/A';

        const hasRPL =
          assessmentResources.some(
            (item) => item.status === StudentResourceEnrolmentStatus.RECOGNITION_OF_PRIOR_LEARNING,
          ) ||
          conversationResources.some(
            (item) => item.status === StudentResourceEnrolmentStatus.RECOGNITION_OF_PRIOR_LEARNING,
          );

        const hasCreditTransfer =
          assessmentResources.some(
            (item) => item.status === StudentResourceEnrolmentStatus.CREDIT_TRANSFER,
          ) ||
          conversationResources.some(
            (item) => item.status === StudentResourceEnrolmentStatus.CREDIT_TRANSFER,
          );

        if (hasRPL) {
          result = 'RPL';
        } else if (hasCreditTransfer) {
          result = 'Credit Transfer';
        } else {
          result = 'Competent';
        }

        return {
          unitTitle: unitOfCompetency?.title,
          result,
          year,
        };
      }),
    };
  }

  if (documentType === 'confirmationOfEnrolment') {
    return {
      name: fullStudentName,
      course: fullCourseName,
      dob: enrolment.user?.studentData?.date_of_birth
        ? djs(enrolment.user?.studentData?.date_of_birth).format('DD/MM/YYYY')
        : 'N/A',
      generationDate: currentDate.format('dddd, DD MMMM YYYY'),
      enrolmentStartDate: djs(enrolment.start_at_date).format('dddd, DD MMMM YYYY'),
      enrolmentEndDate: expectedCourseStartAndEndDates.courseEndDate?.long ?? 'N/A',
    };
  }
}
